<template>
  <div ref="accessibilityModalContainer" class="cx-modal--full accessibility-modal" v-trap-focus="{ escFunc: close, live: true}">
    <div class="cx-modal__body">
      <button class="cx-modal__close cx-btn" @click="close" :aria-label="$t('action.close')">
        <span class="cx-btn__icon">
          <i class="fas fa-times" aria-hidden="true"></i>
        </span>
      </button>
      <div>
        <article>
          <div class="cx-wrap--article">
            <div class="cx-div m-mapitem__header">
              <div class="cx-t20">{{detail.name}}</div>
            </div>
            <!-- START:Map -->
            <div
              v-if="disableMapbox && showMap"
              class="cx-div -round-corner accessibility-modal__map-placeholder"
              aria-hidden="true"
            >
              <cx-img
                tag="i"
                class="m-graphic--ngi"
                imgSrc="/static/graphic/mgto_graphic_location.png"
              ></cx-img>
            </div>
            <div v-else ref="map" id="map" class="cx-div -round-corner" v-show="showMap"></div>
            <!-- END:MAP -->
            
            <div class="cx-tab cx-div">
              <template v-for="(tab, t_idx) in tabs">
                <div
                  tabindex="0"
                  role="tab"
                  :aria-selected="activeTab == t_idx ? 'true' : 'false'"
                  :aria-label="tab.label"
                  :key="tab.label" :class="{ '-active': activeTab == t_idx }"
                  onKeypress="event.key === 'Enter' || event.which === 13 ? this.click() : null"
                  onKeydown="event.key === 'ArrowLeft' || event.which === 37 ? this.previousElementSibling?.focus() : event.key === 'ArrowRight' || event.which === 39 ? this.nextElementSibling?.focus() : null"
                  @click="activeTab = t_idx; activeSection = 0; 
                  pushTracking(tab_view, tabs[t_idx].label, 'tab-' + t_idx, detail)"
                  class="cx-tab__label -center"
                  :id="'tab-' + t_idx"
                >
                  <i :class="tab.icon" aria-hidden="true"></i>
                  <span>{{tab.label}}</span>
                </div>
              </template>
              <template v-for="(tab, t_idx) in tabs">
                <div
                  :key="tab.label + '_content'"
                  class="cx-tab__content"
                  :style="`display: ${activeTab == t_idx ? 'block' : 'none'}`"
                >
                  <div class="cx-row">
                    <div class="cx-col-sm-4" v-if="hasCategories(tab)">
                      <accessibility-sub-categories :data="tab.data" :active="activeSection"
                        @sectionChanged='sectionChanged' />
                    </div>
                    <div v-for="(data, section, d_idx) in tab.data" :key="section + d_idx"
                      v-show="activeSection == d_idx"
                      :class="`cx-col-sm-${hasCategories(tab) ? 8 : 12}`">
                      <template v-for="(tableData, title, a_idx) in filteredTable(data)">
                        <table role="presentation" :key="a_idx" v-if="!isFilteredRowsEmpty(tableData)" class="cx-table -fade cx-div" style="display:table">
                          <thead v-if="Object.keys(filteredTable(data)).length > 1 || title.toLowerCase() !== tab.label.toLowerCase()">
                            <tr>
                              <th colspan="3">{{ title }}</th>
                            </tr>
                          </thead>

                          <template v-if="isNode(tableData)">
                            <accessibility-table :title="title" :rows="filteredRows(tableData)" :settings="settings"/>
                          </template>
                          
                          <template v-else-if="isArrayOfNode(tableData)">
                            <accessibility-table v-for="(item, index) in filteredRows(tableData)" :key="index" :title="title" :rows="item" :settings="settings"/>
                          </template>

                          <template v-else-if="hasSubCategories(tableData)">
                            <template v-for="(item, header, index) in filteredRows(tableData)">
                              <accessibility-table :key="index" :title="title" :rows="item" :header="header" :settings="settings"/>
                            </template>
                          </template>

                          <template v-else-if="hasSubCategoriesArray(tableData)">
                            <template v-for="(subcat, header) in filteredRows(tableData)">
                              <accessibility-table v-for="(item, index) in subcat" :key="index" :title="title" :rows="item" :header="header" :settings="settings"/>
                            </template>
                          </template>
                        </table>
                      </template>

                      <accessibility-icon-table v-if="settings['level-icon-list'] && tab.icon.includes('wheelchair')" :title="settings['level-icon-list']" />
                      <div v-if="tab.remarks" v-html="tab.remarks"></div>

                      <table role="presentation" v-if="tab.label === settings.video.label && Array.isArray(detail.videos) && detail.videos.length > 0" 
                        class="cx-table -fade cx-div" style="display: table;">
                        <thead>
                          <tr><th colspan="3">{{ settings.video.title }}</th></tr>
                        </thead> 
                        <tbody>
                          <cx-slider-v2
                            barPager cycling draggable>
                            <cx-slider-item 
                              v-for="video in detail.videos"
                              :key="video"
                              @active="$refs[video] && $refs[video][0].stop()"
                              @dismiss="$refs[video] && $refs[video][0].stop()">
                              <div class="cx-media--cover cx-div">
                                <div class="cx-media--video">
                                  <cx-video :id="detail.id" :ref="video" controls :src="getMediaHost(video, 'MGTO')" />
                                </div>
                              </div>
                            </cx-slider-item>
                          </cx-slider-v2>
                        </tbody>
                      </table>

                    </div>
                  </div>

                  <div class="cx-list">
                    <div class="cx-list__item">
                      <map-button :coord="coord" v-on:click.native="pushTracking(poi_action, 'Map', $event.target.closest('a').href, detail)"/>
                    </div>

                    <div class="cx-list__item" v-if="showDetail && detail.poiId">
                      <a
                        class="cx-link"
                        tabindex="0"
                        @click.prevent="pushTracking(poi_action, 'Detail', localedURL(detail.poiId), detail);window.open(localedURL(detail.poiId), '_blank')"
                      >
                        <span class="cx-link__icon">
                          <i class="fal fa-archway"></i>
                        </span>
                        <span>{{ $t('common.details') || 'common.details'}}</span>
                      </a>
                    </div>

                    <div class="cx-list__item" v-if="showDetail && detail.poiId">
                      <share-model 
                        :shareUrl="detail.poiId.split('/').slice(1).join('/')"
                        :extralLink="false"
                      >
                        <template slot-scope="{open}">
                          <a href="#" class="cx-link" @click.prevent="pushTracking(poi_action, 'Share', $route.fullPath, detail);open()" role="button">
                            <span class="cx-link__icon">
                              <i class="fal fa-share"></i>
                            </span>
                            <span>{{$t('media.share')}}</span>
                          </a>
                        </template>
                      </share-model>
                    </div>
                  </div>
                </div>
              </template>
              
            </div>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
let mbgl;
if (process.env.VUE_ENV !== 'server') {
  mbgl = require('mapbox-gl');
}
import mapHelper from '@/mixins/mapHelper';
import AccessibilityTable from '@/components/AccessibilityTable';
import AccessibilitySubCategories from '@/components/AccessibilitySubCategories';
import AccessibilityIconTable from '@/components/AccessibilityIconTable';
import MapButton from '@/components/MapButton';
import ShareModel from '@/components/ShareModal';
import restoreFocus from '@/mixins/restoreFocus';
import autoFocus from '@/mixins/autoFocus';
import { TAB_VIEW, POI_ACTION } from '../constants/gtmce';
import videoTracking from '@/mixins/videoTracking';

import { bounds } from './SightMapMapContainer.vue';

export default {
  name: 'accessibility-modal',
  mixins: [mapHelper, restoreFocus, autoFocus, videoTracking],
  props: {
    detail: {
      type: Object,
      default: () => ({})
    },
    settings: {
      type: Object,
    },
  },
  uit() {
    return [
      ['action', ['close']]
    ]
  },
  data() {
    return {
      map: null,
      activeTab: 0,
      activeSection: 0,
      window: window,
      tab_view: TAB_VIEW,
      poi_action: POI_ACTION
    }
  },
  components: {
    AccessibilityTable,
    AccessibilitySubCategories,
    AccessibilityIconTable,
    MapButton,
    ShareModel,
  },
  computed: {
    disableMapbox() {
      return this.$store.state.ui.disableMapbox;
    },
    showMap: function() {
      return this.detail.coordinate && Array.isArray(this.detail.coordinate) && this.detail.coordinate.length == 2;
    },
    tabs() {
      return this.settings.tabs
        .map((tab, index) => {
          return {
            label: tab.name,
            icon: tab.icon,
            remarks: tab.remarks,
            data: { '': this.preprocessSection(
              tab.sections.reduce((object, section) => {
                return {
                  ...object,
                  ...this.traversal(this.detail['infos'], section.split('/'))
                };
              }, {})
            ) },
          };
        })
        .filter(this.shouldShowTab);
    },
    tabLabel: function() {
      return this.tabs.map(tab => tab.label)
    },
    coord() {
      return {
        lon: this.detail.coordinate[1],
        lat: this.detail.coordinate[0],
      };
    },
    showDetail() {
      return this.$route.name.match(/^Accessibility_/);
    },
  },
  methods: {
    pushTracking(event, label, id, detail) {
      const params = {};
      if (event.event === 'tab_view') {
        params.element_name = label;
        params.element_id = id;
        params.parent_name = detail.name;
        params.parent_id = detail.id;
        params.value = 1;
      }
      if (event.event === 'poi_action') {
        params.element_name = id;
        params.element_id = label;
        params.parent_name = detail.name;
        params.parent_id = detail.id;
        params.value = 1;
        // remove lang slug
        if (params.element_name && params.element_name.startsWith('/')) {
          params.element_name = '/' + params.element_name.split('/').slice(2).join('/');
        }
      }
      this.$gtmPush({
        ...event,
        ...params,
        a11yCat: this.detail.category,
        label: label
      })
    },
    traversal(info, path) {
      const target = path.shift();
      if (path.length > 0 && info[target]) {
        return this.traversal(info[target], path);
      } else {
        const result = {};
        result[target] = info[target] ? info[target] : {};
        return result;
      }
    },
    isAccessibilityLevel(data) {
      const list = ['carPark', 'wheelChair', 'visuallyImpaired', 'hearingImpaired', 'locked', 'valetParking'];
      return typeof data === 'object' && !Array.isArray(data)
        && Object.keys(data).every(key => list.includes(key));
    },
    isTrivial(data) {
      return data === null || typeof data === 'string' || typeof data === 'number' || this.isAccessibilityLevel(data);
    },
    isArrayOfString(data, debug = false) {
      if (debug) {
        console.log(data, Array.isArray(data) && data.every(item => (item === null || typeof item !== 'object') && !Array.isArray(item)));
      }
      return Array.isArray(data) && data.every(item => this.isTrivial(item));
    },
    isArrayOfArray(data, debug = false) {
      return Array.isArray(data) && data.every(item => Array.isArray(item));
    },
    isNode(data, debug = false) {
      if (debug) {
        console.log(data, typeof data === 'object' && !Array.isArray(data) && Object.keys(data).every(key => this.isArrayOfString(data[key], debug)));
      }
      return typeof data === 'object' && !Array.isArray(data)
        && Object.keys(data).every(key => this.isArrayOfString(data[key]) || this.isArrayOfArray(data[key]) || this.hasLink(data[key]));
    },
    isArrayOfNode(data, debug = false) {
      if (debug) {
        console.log(data, Array.isArray(data) && data.every(item => this.isNode(item, debug)));
      }
      return Array.isArray(data) && data.every(item => this.isNode(item));
    },
    hasSubCategories(data, debug = false) {
      let result = null;
      if (debug) {
        console.group('hasSubCategories');
        console.log(data);
      }
      result = typeof data === 'object' && !Array.isArray(data)
        && Object.keys(data).every(key => this.isNode(data[key], debug));
      if (debug) {
        console.log(`result: ${result}`)
        console.groupEnd('hasSubCategories');
      }
      return result;
    },
    hasSubCategoriesArray(data, debug = false) {
      let result = null;
      if (debug) {
        console.group('hasSubCategoriesArray');
        console.log(data);
      }
      result = typeof data === 'object' && !Array.isArray(data)
        && Object.keys(data).every(key => this.isArrayOfNode(data[key], debug));
      if (debug) {
        console.log(`result: ${result}`)
        console.groupEnd('hasSubCategoriesArray');
      }
      return result;
    },
    hasLink(data) {
      return Array.isArray(data) && data.length === 5 && data[4] && Array.isArray(data[4]); // https://3.basecamp.com/3633946/buckets/6317965/todos/8374787703
    },
    shouldShowTab(tab) {
      const tabHasNoInfo = Object.keys(tab.data['']).every(key => Object.keys(tab.data[''][key]).length === 0);
      const hasVideo = tab.label === this.settings.video.label && Array.isArray(this.detail.videos) && this.detail.videos.length > 0;
      return !tabHasNoInfo || hasVideo;
    },
    sectionChanged(index) {
      this.activeSection = index;
    },
    hasCategories(tab) {
      return Object.keys(tab.data).some(k => !!k);
    },
    filteredTable(data) {
      const filter = this.settings.ignoredTables;
      let result = data;
      for (let item of filter) {
        if (data[item]) {
          delete data[item];
        }
      }
      return result;
    },
    filteredRows(rows) {
      const copy = rows;
      const filter = this.settings.ignoredRows;
      Object.keys(rows).forEach((row) => {
        if (filter.some(regex => row.match(regex))) {
          delete copy[row];
        }
      });
      return copy;
    },
    isFilteredRowsEmpty(rows) {
      return Object.keys(this.filteredRows(rows)).length === 0;
    },
    isValue(val) {
      return (Array.isArray(val) && (typeof val[0] == 'boolean' 
        || typeof val[0] == 'string' 
        || val[0] == null || this.isValue(val[0])))
    },
    bindMap() {
      if (this.disableMapbox) return;
      this.map = new mbgl.Map({
        container: this.$refs.map,
        style: this.mgtoMapStyle,
        center: this.detail.coordinate,
        zoom: 12,
        maxBounds: bounds,
        trackResize: true,
        localIdeographFontFamily: '"Mark Offc Pro Regular", "Lato Bold", "Mark Offc Pro Medium", "Mark Offc Pro Italic", "Noto Sans", "Arial Unicode MS Regular", sans-serif ',
      });

      this.map.on('style.load', () => {
        this.switchMapLocale();
        this.blackAndWhite && this.switchMapToGrayscale();
        this.flyTo({
          center: this.detail.coordinate,
          zoom: 17
        });
        setTimeout(() => {
          this.projectSightsAsMarker({
            projectID: this.detail.name,
            sights: [this.detail],
            startIdx: -1
          });
        }, 0);
        setTimeout(() => {
          this.activeProjectedMarker({id: 0}); 
          this.projectPOIS({
            projectID: this.detail.name,
          })
        }, 0)
      });
    },
    preprocessTable: function(table) {
      if (this.isTrivial(table)) {
        return table;
      } else if (!Array.isArray(table)) {
        const rowKeys = Object.keys(table);
        rowKeys.forEach(rowKey => {
          if (!Array.isArray(table[rowKey])) {
            table[rowKey] = this.preprocessTable(table[rowKey]);
          }
        })

        return table
      } else {
        return table.map(t => this.preprocessTable(t))
      }
      
    },
    preprocessSection: function(tab) {
      if (this.isNode(tab)) {
        return result;
      }
      Object.keys(tab).forEach(sectionKey => {
        const section = tab[sectionKey];
        Object.keys(section).forEach(tableKey => {
          section[tableKey] = this.preprocessTable(section[tableKey]);
        })
      })

      return tab;
    },
    groupTabs: function(keys) {
      const data = {};

      keys.forEach(key => {
        data[key] = this.detail.infos[key]
      });

      return {'': data};
    },
    close: function() {
      this.$emit('close');
    },
  },
  watch: {
    tabLabel: {
      deep: true,
      handler: function(val) {
        const match = val.findIndex(k => k == this.activeTab);
        if (!match) this.activeTab = 0;
      }
    }
  },
  created() {
    if (!this.detail || !this.detail.id) {
      this.close();
    }
    console.log('detail', this.detail);
  },
  mounted() {
    this.$store.dispatch('ui/FREEZE_BODY');
    !this.disableMapbox && this.showMap && this.bindMap();
    this._af_autoFocus();
    const accessibilityModalContainer = this.$refs.accessibilityModalContainer;
    const createFocusTrap = window.focusTrap && window.focusTrap.createFocusTrap;
    if (createFocusTrap) {
      if (accessibilityModalContainer) {
        this.accessibilityModalTrap = createFocusTrap(accessibilityModalContainer);
      }
    }
    if (this.accessibilityModalTrap) {
      this.accessibilityModalTrap.activate();
    }

    setTimeout(() => {
      const params = {};
      params.element_name = this.detail.name;
      params.element_id = this.detail.id;
      params.slug = null; // will be video src slug (without locale)
      params.parent_name = this.detail.category && this.detail.category[0] || null;
      params.value = 0; // will be played video percentage

      const videos = document.getElementsByTagName('video');
      this.videoTrackingGA4(params, videos, (src) => 'videos/' + src.split('videos/')[1]);
    });
  },
  beforeDestroy() {
    this.$store.dispatch('ui/DISMISS_FREEZE_BODY');
    if (this.accessibilityModalTrap) {
      this.accessibilityModalTrap.deactivate();
    }
  },
}
</script>

<style lang="scss">
.accessibility-modal {
  z-index: 10003 !important;
  #map{
    height: 400px;
    @media(max-width:767px){
      height: 300px;
    }
    @media(max-width:375px){
      height: 200px;
    }
  }
  &__map-placeholder {
    height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(238, 238, 238);
    @media(max-width:767px){
      height: 300px;
    }
    @media(max-width:375px){
      height: 200px;
    }
  }
  .cx-media--video {
    padding-top: 56.25%;
    video {
      position: absolute!important;
      top: 50%!important;
      left: 50%!important;
      transform: translate(-50%,-50%)!important;
      min-width: 100%!important;
      min-height: 100%!important;
      height: auto!important;
      width: 100%;
    }
  }
  td {
    // width: 33.333%;
    width: 30%;
  }
  td:first-child {
    // width: 33.333%;
    width: 30%;
  }
  td[colspan="2"] {
    // width: 66.667%;
    width: 70%;
  }
  tr {
    white-space: pre-wrap;
  }
  tbody{
    background-color: $color-white;

    & + tbody > tr:first-child {
      border-top: 2px solid #343c3f
    }
  }
  .cx-tab{
    background-color: $color-white;

    @media (min-width: $breakpoint-sm){
      .fas{
        font-size: 1.5em;
      }
    }

    .cx-tab__label {
      font-size: inherit;
    }
  }
  .cx-modal{
    &__body{
      margin-top: 0;
    }
  }
  .cx-table {
    border-bottom: 1px solid #b2bcbf;

    thead {
      border-top: 2px solid #b2bcbf;
      border-bottom: 2px solid #b2bcbf;
    }
  }
}
.accessibility-modal .cx-media--video video:-ms-fullscreen {
  transform: none !important;
}

body.-freezed {
	overflow: hidden;
}

.cx-modal__body {
	width: 800px;
	padding: 0 calc((100% - 800px) / 2);
	box-sizing: content-box;
}

.cx-modal--full::after,
.cx-modal::after {
	content: unset;
}

.cx-modal--full {
	background: rgba(255, 255, 255, 0.95) url("data:image/svg+xml;charset=utf-8,%3Csvg width='6' height='6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5 0h1L0 6V5zm1 5v1H5z' fill='%23fff' fill-rule='evenodd'/%3E%3C/svg%3E");
}
</style>

<style>
.cx-tab.cx-div .-active + .cx-tab__content {
  display: none;
}
</style>
